/**
 * سفارشِ رنگ — دیدن (تحویل، PDF، ارسال) و ثبت/ویرایش.
 * کاربر: «پارت‌های 31-8 و 1-9، مجموعِ طاقه فلان، سفارشِ این بار فلان؛ سبز این‌قدر، آبی این‌قدر» ⇒ PDF برای ارسال.
 * سهمِ هر پارت را سرور خودکار از ماندهٔ قدیمی‌ترین پارت برمی‌دارد؛ اینجا همان را پیش‌نمایش می‌دهیم.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button, Collapse, Grid, Input, Modal, Popconfirm, Select, Space, Switch, Tag, message } from "antd";
import {
  CheckOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, PlusOutlined,
  SaveOutlined, SendOutlined,
} from "@ant-design/icons";
import api from "../../api/client";
import { apiError } from "../../utils/apiError";
import { saveBlob } from "../../utils/downloadFile";
import { formatMoney, MoneyInput } from "../../utils/money";
import { JalaliDateInput, todayISO } from "../../utils/dateUtils";
import { useDraft } from "../../utils/draft";
import DraftBar from "../../components/DraftBar";
import { LotLink } from "./DyeLotSheet";
import { BASE, IntInput, QuickPick, STATUS, canShareFile, fa, fetchOrderPdf, lbl, n0, shareOrSave, toEn } from "./dyeShared";

const newColor = () => ({ color_name: null, color_code: null, tagheh: null });
const EMPTY = { order_date: null, code: null, dyehouse: null, driver: null, freight_amount: null, freight_paid: false,
  notes: null, lot_ids: [], colors: [newColor()] };
const byDate = (a, b) => (a.entry_date < b.entry_date ? -1 : a.entry_date > b.entry_date ? 1 : a.id - b.id);
// پیش‌نویس فقط وقتی معنا دارد که رنگی نوشته شده باشد (پارتِ از پیش‌انتخاب‌شده به‌تنهایی پیش‌نویس نیست)
const isEmptyForm = (v) => !v || !(v.colors || []).some((c) => c.color_name || c.tagheh);

function PdfBar({ order }) {
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPdf(null); }, [order.id, order.updated_at_fa, order.delivered]);
  const prepare = async () => { setBusy(true); setPdf(await fetchOrderPdf(order)); setBusy(false); };
  if (!pdf) {
    return <Button block size="large" type="primary" icon={<FilePdfOutlined />} loading={busy} onClick={prepare}>
      فایل PDF سفارش</Button>;
  }
  return (
    <Space.Compact block>
      <Button size="large" type="primary" icon={<SendOutlined />} onClick={() => shareOrSave(pdf, `سفارش رنگرزی ${order.code}`)}>
        {canShareFile(pdf.file) ? "ارسال" : "دانلود"}</Button>
      <Button size="large" icon={<EyeOutlined />} onClick={() => window.open(URL.createObjectURL(pdf.blob), "_blank", "noopener")}>
        مشاهده</Button>
      {canShareFile(pdf.file) && <Button size="large" icon={<DownloadOutlined />} onClick={() => saveBlob(pdf.blob, pdf.name)} />}
    </Space.Compact>
  );
}

function DeliverRow({ c, canEdit, onSaved }) {
  const [v, setV] = useState(c.delivered_tagheh);
  useEffect(() => setV(c.delivered_tagheh), [c.delivered_tagheh]);
  const save = async (val) => {
    try {
      await api.patch(`${BASE}/order-colors/${c.id}/deliver`, { delivered_tagheh: val ?? 0 });
      message.success("تحویل ثبت شد"); onSaved?.();
    } catch (e) { message.error(apiError(e)); }
  };
  const done = c.delivered_tagheh >= c.tagheh;
  return (
    <div className={"dye-color" + (done ? " dye-done" : "")}>
      <div className="dye-color-h">
        <b>{c.color_name}</b>{c.color_code && <span className="dye-opt-code">{fa(c.color_code)}</span>}
        <span className="dye-grow" />
        <span>{fa(c.tagheh)} طاقه</span>
      </div>
      <div className="dye-color-d">
        <span className="dye-mute">تحویل:</span>
        {canEdit ? (
          <>
            <IntInput value={v} onChange={setV} style={{ width: 84 }} size="middle" />
            <Button size="middle" disabled={v === c.delivered_tagheh} onClick={() => save(v)}>ثبت</Button>
            {!done && <Button size="middle" type="primary" ghost icon={<CheckOutlined />} onClick={() => save(c.tagheh)}>همه</Button>}
          </>
        ) : <b>{fa(c.delivered_tagheh)}</b>}
        {c.delivered_date_fa && <span className="dye-mute">{fa(c.delivered_date_fa)}</span>}
      </div>
    </div>
  );
}

export default function DyeOrderSheet({ open, orderId, initialLots, board, options, onClose, onSaved, onOpenLot,
  canEdit, canDelete }) {
  const screens = Grid.useBreakpoint();
  const order = useMemo(() => board.orders.find((o) => o.id === orderId) || null, [board, orderId]);
  const lotsById = useMemo(() => Object.fromEntries(board.lots.map((l) => [l.id, l])), [board]);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dateTouched, setDateTouched] = useState(false);
  const isNew = open && !order;
  const draft = useDraft(isNew ? "dye:order:new" : null, f, { isEmpty: isEmptyForm, enabled: isNew });

  useEffect(() => {
    if (!open) return;
    if (order) {
      setEditing(false);
      setF({ order_date: order.order_date, code: order.code_manual || null, dyehouse: order.dyehouse, driver: order.driver,
        freight_amount: order.freight_amount, freight_paid: order.freight_paid, notes: order.notes,
        lot_ids: order.lots.map((x) => x.lot_id),
        colors: order.colors.map((c) => ({ color_name: c.color_name, color_code: c.color_code, tagheh: c.tagheh, width: c.width })) });
    } else {
      setEditing(true);
      // تاریخِ سفارش = تاریخِ ورودِ قدیمی‌ترین پارت (کاربر: «من 1-8 زده بودم، 9-8 آمد») — قابلِ تغییر
      const first = (initialLots || []).map((id) => lotsById[id]).filter(Boolean).sort(byDate)[0];
      setF({ ...EMPTY, order_date: first?.entry_date || todayISO(), lot_ids: initialLots || [],
        dyehouse: first?.dyehouse || null, colors: [newColor()] });
      setDateTouched(false);
    }
  }, [open, orderId, !!order]);   // eslint-disable-line

  // ماندهٔ قابلِ سفارش: مانده + سهمِ همین سفارش (در ویرایش)
  const mine = useMemo(() => Object.fromEntries((order?.lots || []).map((x) => [x.lot_id, x.tagheh])), [order]);
  const avail = (id) => n0(lotsById[id]?.remaining) + n0(mine[id]);
  const selected = f.lot_ids.map((id) => lotsById[id]).filter(Boolean).sort(byDate);
  const total = f.colors.reduce((s, c) => s + n0(c.tagheh), 0);
  const available = selected.reduce((s, l) => s + Math.max(avail(l.id), 0), 0);
  const plan = useMemo(() => {        // همان تخصیصِ سرور: اول قدیمی‌ترین پارت
    let need = total;
    return selected.map((l) => { const t = Math.min(Math.max(avail(l.id), 0), need); need -= t; return [l, t]; });
  }, [total, selected]);   // eslint-disable-line
  // هر نخ جدا رنگ می‌شود ⇒ بعد از انتخابِ اولین پارت فقط پارت‌های هم‌نخ پیشنهاد می‌شوند
  const yarn = selected.find((l) => l.yarn)?.yarn;
  const lotChoices = board.lots.filter((l) => f.lot_ids.includes(l.id)
    || (avail(l.id) > 0 && (!yarn || !l.yarn || l.yarn === yarn)));
  const autoCode = selected[0] ? selected[0].label : "";
  const pickLots = (v) => setF((s) => {
    const first = v.map((id) => lotsById[id]).filter(Boolean).sort(byDate)[0];
    return { ...s, lot_ids: v, ...(isNew && !dateTouched && first ? { order_date: first.entry_date } : {}) };
  });

  const setColor = (i, patch) => setF((s) => ({ ...s, colors: s.colors.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));

  const save = async () => {
    if (!f.lot_ids.length) return message.warning("پارت(های) سفارش را انتخاب کنید");
    const colors = f.colors.filter((c) => c.color_name || c.tagheh);
    if (!colors.length) return message.warning("حداقل یک رنگ با تعداد بنویسید");
    if (total > available) return message.warning(`ماندهٔ پارت‌ها ${fa(available)} طاقه است؛ سفارش ${fa(total)} طاقه شد`);
    setSaving(true);
    const payload = { order_date: f.order_date, code: f.code || null, dyehouse: f.dyehouse, driver: f.driver,
      freight_amount: f.freight_amount || null, freight_paid: !!f.freight_paid, notes: f.notes,
      lots: f.lot_ids.map((id) => ({ lot_id: id })), colors };
    try {
      const r = order ? await api.put(`${BASE}/orders/${order.id}`, payload) : await api.post(`${BASE}/orders`, payload);
      draft.clear();
      message.success("سفارش ذخیره شد");
      setEditing(false);
      await onSaved?.(r.data.id);
    } catch (e) { message.error(apiError(e)); }
    setSaving(false);
  };
  const deliverAll = async () => {
    try { await api.post(`${BASE}/orders/${order.id}/deliver-all`, {}); message.success("کلِ سفارش تحویل شد"); onSaved?.(order.id); } catch (e) {
      message.error(apiError(e));
    }
  };
  const del = async () => {
    try { await api.delete(`${BASE}/orders/${order.id}`); message.success("سفارش حذف شد"); onClose(); onSaved?.(null); } catch (e) {
      message.error(apiError(e));
    }
  };

  const view = order && !editing && (
    <div className="dye-sheet">
      <div className="dye-row"><span className="dye-k">تاریخ</span><span>{fa(order.order_date_fa)}</span>
        <Tag color={STATUS[order.status].color}>{STATUS[order.status].label}</Tag></div>
      {order.dyehouse && <div className="dye-row"><span className="dye-k">رنگرزی</span><span>{order.dyehouse}</span></div>}
      {(order.driver || order.freight_amount) && (
        <div className="dye-row"><span className="dye-k">حمل تا قرچک</span>
          <span>{order.driver || "—"}{order.freight_amount ? ` · کرایه ${formatMoney(order.freight_amount)}` : ""}</span>
          {order.freight_amount ? <Tag color={order.freight_paid ? "success" : "default"}>{order.freight_paid ? "پرداخت شد" : "پرداخت نشده"}</Tag> : null}
        </div>
      )}
      <div className="dye-sec">پارت‌ها</div>
      {order.lots.map((x) => {
        const l = lotsById[x.lot_id];
        return (
          <div className="dye-row" key={x.lot_id}>
            <LotLink lot={l} onOpen={onOpenLot} />
            <span className="dye-mute">{fa(l?.yarn || "")}{l?.opening ? ` · دهانه ${fa(l.opening)}` : ""}</span>
            <span className="dye-grow" /><b>{fa(x.tagheh)}</b><span className="dye-mute">از {fa(n0(l?.tagheh) + n0(l?.transfer_in))}</span>
          </div>
        );
      })}
      <div className="dye-sec dye-row" style={{ borderBottom: 0 }}>
        <span>رنگ‌ها — جمع {fa(order.total)} طاقه · تحویل {fa(order.delivered)}</span>
        <span className="dye-grow" />
        {canEdit && order.status !== "delivered" && (
          <Popconfirm title="همهٔ رنگ‌های این سفارش «تحویل شد» بخورد؟" onConfirm={deliverAll}>
            <Button size="small" type="primary" ghost icon={<CheckOutlined />}>تحویلِ کامل</Button>
          </Popconfirm>
        )}
      </div>
      {order.colors.map((c) => <DeliverRow key={c.id} c={c} canEdit={canEdit} onSaved={() => onSaved?.(order.id)} />)}
      {order.notes && <div className="dye-mute" style={{ marginTop: 8 }}>توضیح: {order.notes}</div>}
      <div style={{ marginTop: 14 }}><PdfBar order={order} /></div>
      <div className="dye-stamp">
        ثبت: {fa(order.created_at_fa)}{order.created_by ? ` (${order.created_by})` : ""}
        {order.updated_at_fa && order.updated_at_fa !== order.created_at_fa
          ? ` — آخرین ویرایش: ${fa(order.updated_at_fa)}${order.updated_by ? ` (${order.updated_by})` : ""}` : ""}
      </div>
      {canEdit && (
        <div className="dye-actions">
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>ویرایش سفارش</Button>
          {canDelete && <Popconfirm title="سفارش حذف شود؟ طاقه‌هایش به ماندهٔ پارت برمی‌گردد." onConfirm={del}>
            <Button danger icon={<DeleteOutlined />}>حذف</Button></Popconfirm>}
        </div>
      )}
    </div>
  );

  const form = editing && (
    <div className="dye-sheet">
      {isNew && <DraftBar draft={draft} what="سفارشِ نیمه‌کاره" onRestore={(v) => v && setF(v)} />}
      <div className="dye-form">
        <div className="dye-f"><span>پارت‌ها (هم‌نخ)</span>
          <Select size="large" mode="multiple" showSearch optionFilterProp="label" style={{ width: "100%" }}
            placeholder="پارت(های) خامی که رنگ می‌شود" value={f.lot_ids}
            onChange={pickLots}
            optionLabelProp="short"
            options={lotChoices.map((l) => ({ value: l.id, short: lbl(l.label),
              label: `${lbl(l.label)} · ${fa(l.yarn || "")} · مانده ${fa(avail(l.id))}${l.track ? ` · رهگیری ${fa(l.track)}` : ""}` }))} />
        </div>
      </div>
      {selected.length > 0 && (
        <div className="dye-plan">
          {plan.map(([l, t]) => <span key={l.id}>{lbl(l.label)}: <b>{fa(t)}</b>/{fa(avail(l.id))}</span>)}
        </div>
      )}
      <div className="dye-form" style={{ marginTop: 10 }}>
        <div className="dye-f dye-half"><span>تاریخ سفارش</span>
          <JalaliDateInput value={f.order_date} onChange={(v) => { setDateTouched(true); setF((s) => ({ ...s, order_date: v })); }}
            style={{ width: "100%", minHeight: 40 }} /></div>
        <div className="dye-f dye-half"><span>شمارهٔ سفارش</span>
          <Input size="large" value={fa(f.code)} placeholder={autoCode ? `خودکار: ${lbl(autoCode)}` : "خودکار از تاریخِ پارت"}
            onChange={(e) => setF((s) => ({ ...s, code: toEn(e.target.value) || null }))} allowClear /></div>
        <div className="dye-f"><span>رنگرزی</span>
          <QuickPick value={f.dyehouse} onChange={(v) => setF((s) => ({ ...s, dyehouse: v }))} options={options.dyehouse} /></div>
      </div>

      <div className="dye-sec">رنگ‌ها</div>
      {f.colors.map((c, i) => (
        <div className="dye-color-edit" key={i}>
          <QuickPick value={c.color_name} placeholder="رنگ (سبز، یشمی، …)" options={options.color}
            onChange={(v) => setColor(i, { color_name: v })}
            onPickOption={(o) => o.code && setColor(i, { color_name: o.value, color_code: o.code })} />
          <div className="dye-color-edit-r">
            <Input size="large" placeholder="کد رنگ" value={fa(c.color_code)} onChange={(e) => setColor(i, { color_code: toEn(e.target.value) || null })} />
            <IntInput placeholder="طاقه" value={c.tagheh} onChange={(v) => setColor(i, { tagheh: v })} />
            <Button size="large" type="text" danger icon={<DeleteOutlined />} disabled={f.colors.length < 2}
              onClick={() => setF((s) => ({ ...s, colors: s.colors.filter((_, j) => j !== i) }))} />
          </div>
        </div>
      ))}
      <Button block icon={<PlusOutlined />} onClick={() => setF((s) => ({ ...s, colors: [...s.colors, newColor()] }))}>رنگِ دیگر</Button>

      <div className={"dye-total" + (total > available ? " dye-neg" : "")}>
        <span>مانده‌ی پارت‌ها <b>{fa(available)}</b></span>
        <span>سفارش <b>{fa(total)}</b></span>
        <span>باقی <b>{fa(available - total)}</b></span>
      </div>
      <Collapse ghost size="small" defaultActiveKey={f.driver || f.freight_amount ? ["ship"] : []} items={[{
        key: "ship", label: "حمل تا قرچک (اختیاری): راننده، کرایه",
        children: (
          <div className="dye-form">
            <div className="dye-f"><span>راننده</span>
              <QuickPick value={f.driver} onChange={(v) => setF((s) => ({ ...s, driver: v }))} options={options.driver}
                placeholder="نامِ راننده" /></div>
            <div className="dye-f dye-half"><span>کرایه (ریال)</span>
              <MoneyInput size="large" style={{ width: "100%" }} value={f.freight_amount}
                onChange={(v) => setF((s) => ({ ...s, freight_amount: v }))} /></div>
            <div className="dye-f dye-half dye-switch"><Switch checked={!!f.freight_paid}
              onChange={(v) => setF((s) => ({ ...s, freight_paid: v }))} /><span>کرایه پرداخت شد</span></div>
          </div>
        ),
      }]} />
      <div className="dye-f"><span>توضیح</span>
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={f.notes ?? ""} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value || null }))} /></div>
      <div className="dye-actions">
        <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={save}>
          {order ? "ذخیرهٔ سفارش" : "ثبت سفارش"}</Button>
        {order && <Button size="large" onClick={() => setEditing(false)}>انصراف</Button>}
      </div>
    </div>
  );

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnHidden
      className={screens.md ? "dye-modal" : "gx-modal-full dye-modal"} width={screens.md ? 620 : "100vw"}
      title={order ? <span>سفارش <b>{lbl(order.code)}</b></span> : "سفارشِ تازهٔ رنگ"}>
      {view || form}
    </Modal>
  );
}
